import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

export interface ProfileVideo {
  id: string;
  user_id: string;
  storage_path: string;
  video_url?: string;
  duration_seconds?: number;
  is_intro_video?: boolean;
  visible_before_match: boolean;
  created_at: string;
}

export const useProfileVideos = (userId?: string) => {
  const { user } = useAuthContext();
  const [videos, setVideos] = useState<ProfileVideo[]>([]);
  const [loading, setLoading] = useState(true);

  const targetUserId = userId || user?.id;

  // Fetch user's videos
  const fetchVideos = useCallback(async () => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const { data, error } = await supabase
      .from("profile_videos")
      .select("*")
      .eq("user_id", targetUserId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching videos:", error);
    } else {
      // Transform to ensure visible_before_match has a value
      const transformed = (data || []).map((v: any) => ({
        ...v,
        visible_before_match: v.visible_before_match ?? false,
      }));
      setVideos(transformed);
    }
    setLoading(false);
  }, [targetUserId]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // Toggle visibility before match
  const togglePreMatchVisibility = useCallback(async (videoId: string, visible: boolean) => {
    if (!user) return false;

    // Check how many are currently visible
    const currentVisibleCount = videos.filter(v => v.visible_before_match && v.id !== videoId).length;
    
    // If trying to make visible and already have 2, reject
    if (visible && currentVisibleCount >= 2) {
      toast({
        title: "Max 2 videos before match",
        description: "You can only show up to 2 videos before matching. Toggle off another video first.",
        variant: "destructive",
      });
      return false;
    }

    // Update in database
    const { error } = await supabase
      .from("profile_videos")
      .update({ visible_before_match: visible })
      .eq("id", videoId)
      .eq("user_id", user.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return false;
    }

    // Update local state
    setVideos(prev => prev.map(v => 
      v.id === videoId ? { ...v, visible_before_match: visible } : v
    ));

    toast({
      title: visible ? "Video visible before match" : "Video hidden until match",
      description: visible 
        ? "This video will be shown to everyone" 
        : "This video will only be visible after you match",
    });

    return true;
  }, [user, videos]);

  // Get videos filtered by match status
  const getVisibleVideos = useCallback((isMatched: boolean) => {
    if (isMatched) {
      // After match: show all videos
      return videos;
    }
    // Before match: only show videos marked as visible_before_match
    return videos.filter(v => v.visible_before_match);
  }, [videos]);

  // Upload a new video
  const uploadVideo = useCallback(async (file: File, visibleBeforeMatch: boolean = false): Promise<string | null> => {
    if (!user) {
      toast({ title: "Not authenticated", variant: "destructive" });
      return null;
    }

    if (!file.type.startsWith("video/")) {
      toast({ title: "Please select a video file", variant: "destructive" });
      return null;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({ title: "Video must be less than 50MB", variant: "destructive" });
      return null;
    }

    const fileExt = file.name.split(".").pop() || "mp4";
    const fileName = `${user.id}/${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-videos")
      .upload(fileName, file, { upsert: true });

    if (uploadError) {
      toast({ title: "Upload failed", description: uploadError.message, variant: "destructive" });
      return null;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("profile-videos")
      .getPublicUrl(fileName);

    // Save to profile_videos table
    const { error: dbError } = await supabase
      .from("profile_videos")
      .insert({ 
        user_id: user.id, 
        storage_path: fileName,
        video_url: publicUrl,
        visible_before_match: visibleBeforeMatch,
      });

    if (dbError) {
      console.error("Error saving video record:", dbError);
      toast({ title: "Error", description: dbError.message, variant: "destructive" });
      return null;
    }

    // Refresh videos list
    await fetchVideos();
    
    toast({ title: "Video uploaded!", description: "Your video is now live" });
    return publicUrl;
  }, [user, fetchVideos]);

  // Delete a video
  const deleteVideo = useCallback(async (videoId: string): Promise<boolean> => {
    if (!user) return false;

    const video = videos.find(v => v.id === videoId);
    if (!video) return false;

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from("profile-videos")
      .remove([video.storage_path]);

    if (storageError) {
      console.error("Storage delete error:", storageError);
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from("profile_videos")
      .delete()
      .eq("id", videoId)
      .eq("user_id", user.id);

    if (dbError) {
      toast({ title: "Error", description: dbError.message, variant: "destructive" });
      return false;
    }

    // Update local state
    setVideos(prev => prev.filter(v => v.id !== videoId));
    toast({ title: "Video deleted" });
    return true;
  }, [user, videos]);

  return {
    videos,
    loading,
    fetchVideos,
    togglePreMatchVisibility,
    getVisibleVideos,
    uploadVideo,
    deleteVideo,
    preMatchCount: videos.filter(v => v.visible_before_match).length,
    totalCount: videos.length,
  };
};
